"use client";

/**
 * Footer.tsx
 *
 * Orchestrator component for the Royal Blue theme footer.
 *
 * Layout:
 *  - 4-column grid (Brand, Schedule, Private Dining, Contact)
 *  - FooterBottom with copyright and legal links
 *
 * Data:
 *  - Fetches dynamic content from the Redux content store.
 *  - Provides sensible fallback values for all fields.
 *  - Includes complex logic for parsing potentially nested address data.
 */

import React from "react";
import { useAppSelector } from "@/store/hooks";

import FooterBrand from "./FooterBrand";
import FooterSchedule from "./FooterSchedule";
import FooterDining from "./FooterDining";
import FooterContact from "./FooterContact";
import FooterBottom from "./FooterBottom";

// ---------------------------------------------------------------------------
// Fallbacks & Constants
// ---------------------------------------------------------------------------

const FALLBACK_SCHEDULE = [
  { days: "Mon - Wed", time: "11:00 AM - 10:00 PM" },
  { days: "Thu - Fri", time: "11:00 AM - 11:30 PM" },
  { days: "Sat", time: "12:00 PM - 12:00 AM" },
  { days: "Sun", time: "Closed" },
];

const SOCIAL_DEFAULTS = {
  facebook: "#",
  instagram: "#",
  twitter: "#",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalises the address from string OR object (Mammamia/CMS format) to a string.
 */
function parseAddress(raw: any): string {
  if (typeof raw === "string") return raw;
  if (!raw || typeof raw !== "object") {
    return "123 Culinary St., Foodie District, Gourmet City";
  }

  // Try joining street and city properties
  const joined = [raw.street, raw.city].filter(Boolean).join(", ");
  return joined || "123 Culinary St., Foodie District, Gourmet City";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Footer = () => {
  const { data: content } = useAppSelector((state: any) => state.content);
  const data = content?.royalBlueFooter || content?.footer || {};

  // ── Column 1: Brand ──────────────────────────────────────────────────────
  const rawSocialLinks = data?.brand?.socialLinks || [];
  const socialData = data?.brand?.social || data?.social || SOCIAL_DEFAULTS;

  let socialLinks =
    Array.isArray(rawSocialLinks) && rawSocialLinks.length > 0
      ? rawSocialLinks.map((link: any) => ({
          name: link.label || link.name,
          href: link.href || "#",
        }))
      : [
          { name: "Facebook", href: socialData.facebook || "#" },
          { name: "Instagram", href: socialData.instagram || "#" },
          { name: "Twitter", href: socialData.twitter || "#" },
        ];

  // ── Column 2: Schedule ───────────────────────────────────────────────────
  const rawSchedule = data?.openingHours?.schedule;
  const scheduleToRender =
    Array.isArray(rawSchedule) && rawSchedule.length > 0
      ? rawSchedule
      : FALLBACK_SCHEDULE;

  // ── Column 3: Private Dining ──────────────────────────────────────────────
  const privateDining = data?.privateDining || {};

  // ── Column 4: Contact ───────────────────────────────────────────────────
  const contact = data?.contactUs || data?.contact || {};

  // ── Bottom: Bar ────────────────────────────────────────────────────────
  const bottom = data?.bottom || {};

  return (
    <footer
      id="footer"
      className="bg-[#1a1a1a] text-white py-24 border-t border-white/5 overflow-hidden"
    >
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          <FooterBrand
            title={data?.brand?.title || "Royal Blue"}
            description={
              data?.brand?.description ||
              "Experience the finest dining with a blend of tradition and modern elegance."
            }
            socialLinks={socialLinks}
          />

          <FooterSchedule
            title={data?.openingHours?.title || "Opening Hours"}
            schedule={scheduleToRender}
          />

          <FooterDining
            title={privateDining.title || "Private Dining"}
            description={
              privateDining.description ||
              "Special event? Our private dining rooms offer the perfect setting."
            }
            buttonLabel={privateDining.buttonLabel || "Explore Rooms"}
            buttonHref={privateDining.buttonHref || "#rooms"}
          />

          <FooterContact
            title={contact.title || "Contact Us"}
            address={parseAddress(contact.address)}
            phone={contact.phone || "+1 234 567 890"}
            email={contact.email || "info@elroyale.com"}
          />
        </div>

        <FooterBottom
          copyright={
            bottom.copyright ||
            "© 2026 Royal Blue Restaurant. All rights reserved."
          }
          privacy={{
            label: bottom.privacyPolicy?.label || "Privacy Policy",
            href: bottom.privacyPolicy?.href || "/privacy-policy",
          }}
          terms={{
            label: bottom.termsOfUse?.label || "Terms of Use",
            href: bottom.termsOfUse?.href || "/terms-conditions",
          }}
        />
      </div>
    </footer>
  );
};

export default Footer;
